package com.tovtalk.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.systemBarsPadding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.tovtalk.utils.language.syncAppLanguageToPlatform
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.data.local.TokenManager
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.presentation.viewmodel.auth.AuthViewModel
import com.tovtalk.ui.components.AuthBottomSheet
import com.tovtalk.ui.navigation.TovTalkNavHost
import com.tovtalk.ui.platform.tryCompleteGoogleRedirectLoginIfAny
import com.tovtalk.utils.config.isWeb
import com.tovtalk.ui.LocalAppInForeground
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.ui.theme.LocalTovTalkFontFamily
import com.tovtalk.ui.theme.LocalTovTalkHebrewFontFamily
import com.tovtalk.ui.theme.LocalTovTalkLatinFontFamily
import com.tovtalk.utils.config.isFontDebugEnabled
import com.tovtalk.utils.deeplink.DeepLinkRoute
import com.tovtalk.utils.language.LanguageCodes
import com.tovtalk.utils.language.LocalAppLanguage
import com.tovtalk.utils.logger.Logger

/**
 * Root composable for the app - shared across Android, iOS, Web.
 * Same UI code everywhere; platform passes callbacks (Toast, locale, etc.).
 * Provides LocalAppLanguage from SettingsRepository.observeLanguage().
 *
 * On Web, pass [initialLanguage] from localStorage (e.g. "he") so the first frame uses the correct
 * font and avoids gibberish after refresh when Hebrew was selected.
 *
 * When [debugFont] is true (Web: localStorage["tovtalk_debug_font"]=1 or ?debug_font=1), logs language/font and shows a small overlay.
 */
@Composable
fun TovTalkApp(
    pendingDeepLink: DeepLinkRoute? = null,
    onDeepLinkConsumed: () -> Unit = {},
    onShowError: (String) -> Unit = {},
    onLanguageApplied: (String) -> Unit = {},
    onPhotoUploaded: ((Int) -> Unit)? = null,
    onOpenUrl: (String) -> Unit = {},
    onReelsLoaded: ((List<Post>) -> Unit)? = null,
    onReelIndexChanged: ((Int, List<Post>) -> Unit)? = null,
    onPreloadNextVideoUrl: ((String?) -> Unit)? = null,
    onDiscoverFirstPage: ((List<Post>) -> Unit)? = null,
    isAppInForeground: Boolean = true,
    initialLanguage: String? = null,
    debugFont: Boolean = false,
) {
    val settingsRepository: SettingsRepository = koinInject()
    val detectedInitial = remember {
        initialLanguage ?: settingsRepository.getCurrentLanguage()
    }
    val language by settingsRepository.observeLanguage().collectAsState(
        initial = detectedInitial
    )

    LaunchedEffect(language) {
        syncAppLanguageToPlatform(language)
        // Translations loaded once in SplashScreen; when user changes language, load in LanguageSelectionScreen or SettingsScreen
    }

    // Google redirect completion. On web, if a Google sign-in redirect is pending
    // (we previously navigated away to Firebase), complete the backend login on
    // next page load. LoginScreen used to do this; now AuthBottomSheet is only
    // mounted on-demand, so we do it here at the top-level composable.
    val authViewModel: AuthViewModel = koinInject()
    LaunchedEffect(Unit) {
        if (isWeb() && TokenManager.accessToken == null) {
            tryCompleteGoogleRedirectLoginIfAny { token ->
                authViewModel.loginWithFirebaseToken(token)
            }
        }
    }

    val layoutDirection = if (LanguageCodes.isRtl(language)) LayoutDirection.Rtl else LayoutDirection.Ltr
    val latinFont = LocalTovTalkLatinFontFamily.current
    val hebrewFont = LocalTovTalkHebrewFontFamily.current
    // Theme font by language so all Text() (Login, etc.) use correct script. Language selection uses TovTalkText for "עברית" so Hebrew option still shows correctly when theme is Latin.
    val themeFont = when {
        LanguageCodes.isRtl(language) && hebrewFont != null -> hebrewFont
        latinFont != null -> latinFont
        hebrewFont != null -> hebrewFont
        else -> null
    }
    val fontKind = when (themeFont) {
        hebrewFont -> "hebrew"
        latinFont -> "latin"
        else -> "null"
    }
    if (debugFont && isFontDebugEnabled()) {
        Logger.d(
            "TovTalkApp: initialLanguage=$initialLanguage, language=$language, themeFont=$fontKind, hebrewFont!=null=${hebrewFont != null}, latinFont!=null=${latinFont != null}",
            "FontDebug"
        )
    }
    CompositionLocalProvider(
        LocalAppLanguage provides language,
        LocalLayoutDirection provides layoutDirection,
        LocalTovTalkFontFamily provides themeFont,
        LocalAppInForeground provides isAppInForeground
    ) {
        TovTalkTheme {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = MaterialTheme.colorScheme.background
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .systemBarsPadding(),
                ) {
                    TovTalkNavHost(
                        pendingDeepLink = pendingDeepLink,
                        onDeepLinkConsumed = onDeepLinkConsumed,
                        onShowError = onShowError,
                        onLanguageApplied = onLanguageApplied,
                        onPhotoUploaded = onPhotoUploaded,
                        onOpenUrl = onOpenUrl,
                        onReelsLoaded = onReelsLoaded,
                        onReelIndexChanged = onReelIndexChanged,
                        onPreloadNextVideoUrl = onPreloadNextVideoUrl,
                        onDiscoverFirstPage = onDiscoverFirstPage,
                    )

                    // Global auth bottom sheet. Visible state is driven by [AuthSheetController],
                    // which any screen can `open()` when a guest attempts a restricted action.
                    // The sheet itself wires the provider buttons to AuthViewModel.
                    val authSheetController: AuthSheetController = koinInject()
                    AuthBottomSheet(
                        controller = authSheetController,
                        onShowError = onShowError,
                    )
                    if (debugFont) {
                        Box(
                            modifier = Modifier
                                .align(Alignment.TopEnd)
                                .padding(8.dp)
                                .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = TovTalkTheme.opacity.nearFull))
                                .padding(8.dp),
                            contentAlignment = Alignment.CenterEnd
                        ) {
                            Text(
                                text = "FontDebug | lang=$language | font=$fontKind",
                                fontSize = 11.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        }
    }
}
